/**
 * Intenção de download de avaliação pública que precisa sobreviver à
 * autenticação.
 *
 * O ConversionModal autentica "sem sair da página", mas os dois caminhos reais
 * de cadastro saem dela: o Google OAuth volta em `/app`, e a confirmação de
 * e-mail chega por `/auth/confirm` → `/app`. Sem isto o professor que veio da
 * busca atrás de uma prova se cadastrava e nunca a baixava.
 *
 * Gravada quando a pessoa INICIA a autenticação no modal (não quando o modal
 * abre, para quem só olhou não ser redirecionado depois) e consumida uma única
 * vez ao entrar no app.
 */

const STORAGE_KEY = 'pendingPublicDownload'

/** O link de confirmação costuma ser aberto no mesmo dia. */
export const PENDING_DOWNLOAD_TTL_MS = 24 * 60 * 60 * 1000

/** Parâmetro que faz a página da avaliação reabrir o modal de download. */
export const DOWNLOAD_QUERY_PARAM = 'baixar'

interface PendingDownload {
  slug: string
  savedAt: number
}

export const savePendingDownload = (slug: string): void => {
  if (!slug) return
  try {
    const value: PendingDownload = { slug, savedAt: Date.now() }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
  } catch {
    // modo privado / storage bloqueado: cai no fluxo antigo, sem quebrar
  }
}

export const clearPendingDownload = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // idem
  }
}

/** Slug pendente e ainda válido, ou null. Entrada vencida ou corrompida é apagada. */
export const readPendingDownload = (now: number = Date.now()): string | null => {
  let raw: string | null
  try {
    raw = localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as Partial<PendingDownload>
    const valido =
      typeof parsed.slug === 'string' &&
      parsed.slug.length > 0 &&
      typeof parsed.savedAt === 'number' &&
      now - parsed.savedAt >= 0 &&
      now - parsed.savedAt < PENDING_DOWNLOAD_TTL_MS
    if (valido) return parsed.slug as string
  } catch {
    // JSON inválido: trata como ausente
  }

  clearPendingDownload()
  return null
}

/** Lê e apaga: a retomada acontece no máximo uma vez. */
export const consumePendingDownload = (now: number = Date.now()): string | null => {
  const slug = readPendingDownload(now)
  if (slug) clearPendingDownload()
  return slug
}

export const publicDownloadPath = (slug: string): string =>
  `/avaliacoes/${encodeURIComponent(slug)}?${DOWNLOAD_QUERY_PARAM}=1`
